import { h, KeepAlive } from 'vue';
import { RouterView } from 'vue-router';
/* eslint-disable-next-line import/no-cycle */
import viewTypeConstants from '@/constants/viewTypes';
import { store } from '@/appContext';
import { getOffersActiveTabKey, SELLING_DEALERSHIP_TYPES } from '@/constants/offers';
import applyDealerIdQueryString from '@/utilities/myacv/applyDealerIdQueryString';

function getDefaultOffersTab(user) {
  const type = Number.parseInt(user?.defaultDealer?.dealershipType, 10);
  return SELLING_DEALERSHIP_TYPES.has(type) ? 'selling' : 'buying';
}

function limitedUserRedirect() {
  return store.getters.isLimitedUser ? '/auctions/active' : undefined;
}

// handled in the beforeEnters to continue route navigation
// with a boolean check if a redirection is necessary
function proceedOrRedirect(next, redirect) {
  if (redirect) {
    next(redirect);
  } else {
    next();
  }
}

async function getMyInventoryComponent() {
  const myInventoryComponent = await import('@/views/MyAcv/MyInventoryMfeInterface.vue');
  return myInventoryComponent.default;
}

async function enableSellerControlRoom() {
  const enableSellerControlRoomWeb = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.enableSellerControlRoomWeb,
  );

  return enableSellerControlRoomWeb;
}

async function showAcquisitionsPage() {
  const showMyAcvAcquisitionsPage = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.showMyAcvAcquisitionsPage,
  );
  return showMyAcvAcquisitionsPage;
}

const myAcvRoutes = [{
  path: '/myacv',
  component: () => import('@/views/MyAcv/MyAcv.vue'),
  meta: {
    title: 'My ACV',
    shortTitle: 'MyACV',
    linkText: 'My ACV',
  },
  children: [
    {
      path: '',
      name: 'MyACV',
      component: () => import('@/views/MyAcv/MyAcvMenu.vue'),
      async beforeEnter(to, from, next) {
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
      meta: {
        title: 'My ACV',
        shortTitle: 'MyACV',
        linkText: 'My ACV',
      },
    },
    {
      path: 'myinventory',
      name: 'MyAcvMyInventoryParent',
      component: { render: () => h(RouterView) },
      meta: {
        title: 'My Inventory',
        shortTitle: 'My Inventory',
        description: 'Your inventory feed',
        linkText: '<span class="hidden-xs">My&nbsp;</span>Inventory',
        hideMyACVChat: true,
        allowAuctionCommonModals: true,
        sellerOnly: true,
        hasExport: false,
        divider: true,
      },
      async beforeEnter(to, from, next) {
        // Only redirect when hitting the parent directly; children handle themselves
        if (to.name !== 'MyAcvMyInventoryParent') {
          next();
          return;
        }
        if (await showAcquisitionsPage()) {
          next({ name: 'Inventory', query: to.query });
        } else {
          next({ path: '/myacv/myinventory' });
        }
      },
      children: [
        {
          path: '',
          name: 'MyAcvMyInventory',
          component: getMyInventoryComponent,
          meta: {
            hideMyACVChat: true,
            allowAuctionCommonModals: true,
            sellerOnly: true,
            hasExport: false,
          },
          async beforeEnter(to, from, next) {
            if (await showAcquisitionsPage()) {
              next({ name: 'Inventory', query: to.query });
              return;
            }
            const routeEnabled = await store.dispatch(
              'getFlag',
              store.state.featureFlags.featureFlagSlugs.myInventoryEnabled,
            );
            const redirect = await applyDealerIdQueryString(to, from);
            if (redirect) {
              next(redirect);
            } else if (routeEnabled) {
              next();
            } else {
              next({ name: 'MyAcvActiveBuying' });
            }
          },
        },
        {
          path: 'inventory',
          name: 'Inventory',
          component: getMyInventoryComponent,
          meta: {
            title: 'Inventory',
            shortTitle: 'Inventory',
            description: 'Your inventory feed',
            linkText: 'Inventory',
            hideMyACVChat: true,
            allowAuctionCommonModals: true,
            sellerOnly: true,
          },
          async beforeEnter(to, from, next) {
            if (!await showAcquisitionsPage()) {
              next({ path: '/myacv/myinventory' });
              return;
            }
            const routeEnabled = await store.dispatch(
              'getFlag',
              store.state.featureFlags.featureFlagSlugs.myInventoryEnabled,
            );
            const redirect = await applyDealerIdQueryString(to, from);
            if (redirect) {
              next(redirect);
            } else if (routeEnabled) {
              next();
            } else {
              next({ name: 'MyAcvActiveBuying' });
            }
          },
        },
        {
          path: 'acquisitions',
          name: 'Acquisitions',
          component: () => import('@/views/MyAcv/OffersConsole/OffersConsolePage.vue'),
          meta: {
            title: 'Acquisitions',
            shortTitle: 'Acquisitions',
            description: 'Your acquisitions feed',
            linkText: 'Acquisitions',
            hideMyACVChat: true,
            allowAuctionCommonModals: true,
            sellerOnly: true,
          },
          async beforeEnter(to, from, next) {
            const redirect = await applyDealerIdQueryString(to, from);
            if (redirect) {
              next(redirect);
            } else if (await showAcquisitionsPage()) {
              next();
            } else {
              next({ path: '/myacv' });
            }
          },
          children: [
            {
              path: 'summary',
              name: 'AcquisitionSummary',
              component: () => import('@/views/MyAcv/OffersConsole/SummaryView.vue'),
              meta: {
                title: 'Acquisition Summary',
                shortTitle: 'Summary',
                hideMyACVChat: true,
                sellerOnly: true,
              },
              async beforeEnter(to, from, next) {
                if (await showAcquisitionsPage()) {
                  next();
                } else {
                  next({ path: '/myacv' });
                }
              },
            },
          ],
        },

      ],
    },
    {
      path: 'saved',
      name: 'MyAcvSaved',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
      meta: {
        title: 'Saved Auctions',
        shortTitle: 'Saved',
        description: 'Auctions you\'ve saved',
        linkText: 'Saved<span class="hidden-xs">&nbsp;Auctions</span>',
        sellerOnly: true,
        hasExport: true,
        allowAuctionCommonModals: true,
      },
    },
    {
      path: 'request-inspection',
      name: 'MyAcvRequestInspectionParent',
      component: () => import('@/views/MyAcv/RequestInspection.vue'),
      redirect: '/myacv/request-inspection/new',
      async beforeEnter(to, from, next) {
        const redirect = await applyDealerIdQueryString(to, from);
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.showDigitalRunSheet,
        );
        if (redirect) {
          next(redirect);
        } else if (routeEnabled) {
          next();
        } else {
          next({ name: 'MyAcvActiveBuying' });
        }
      },
      meta: {
        title: 'Request Inspection',
        shortTitle: 'Request Inspection',
        description: 'Request an Inspection to Sell Your Vehicle',
        linkText: '<span class="hidden-xs">Request&nbsp;</span>Inspection',
        sellerOnly: true,
        hasExport: false,
        divider: true,
      },
      children: [
        {
          path: 'new',
          name: 'MyAcvRequestInspection',
          component: () => import('@/views/MyAcv/RequestInspection.vue'),
          meta: {
            title: 'New',
            shortTitle: 'New',
            description: 'Request a New Inspection to Sell Your Vehicle',
            linkText: 'New',
            viewType: 'new',
            sellerOnly: true,
          },
        },
        {
          path: 'requested',
          name: 'MyAcvRequestedInspections',
          component: () => import('@/views/MyAcv/RequestInspection.vue'),
          meta: {
            title: 'Requested',
            shortTitle: 'Requested',
            description: 'Submitted Inspection Requests',
            linkText: 'Requested',
            viewType: 'requested',
            sellerOnly: true,
          },
        },
      ],
    },
    {
      path: 'seller-control-room',
      name: 'MyAcvSellerControlRoom',
      component: () => import('@/views/SellerControlRoom/SellerControlRoom.vue'),
      meta: {
        title: 'Seller Control Room',
        shortTitle: 'Seller Control Room',
        linkText: 'Seller Control Room',
        description: 'Your Sales Activity',
        sellerOnly: true,
        allowAuctionCommonModals: true,
        hideMyACVChatOnMobileWhileModalOpen: true,
      },
      async beforeEnter(to, from, next) {
        if (!await enableSellerControlRoom()) {
          next({ name: 'MyAcvActiveSelling' });
          return;
        }
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
    },
    {
      path: 'active',
      name: 'MyAcvActive',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      redirect: '/myacv/active/buying',
      meta: {
        title: 'My ACV',
        shortTitle: 'Active',
        description: 'Auctions currently buying or selling',
        linkText: 'Active<span class="hidden-xs">&nbsp;Auctions</span>',
      },
      children: [
        {
          path: 'buying',
          name: 'MyAcvActiveBuying',
          component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
          meta: {
            title: 'Active Buying',
            shortTitle: 'Buying',
            noAuctionsDisplayName: 'Active Buying',
            noAuctionsHeader: 'No Active Buying Auctions',
            noAuctionsMessage: 'You don\'t currently have any active buying auctions. When you do, they\'ll appear here.',
            linkText: 'Buying',
            allowAuctionCommonModals: true,
          },
          async beforeEnter(to, from, next) {
            proceedOrRedirect(
              next,
              limitedUserRedirect() ?? await applyDealerIdQueryString(to, from),
            );
          },
        },
        {
          path: 'selling',
          name: 'MyAcvActiveSelling',
          component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
          async beforeEnter(to, from, next) {
            if (await enableSellerControlRoom()) {
              next({ name: 'MyAcvSellerControlRoom' });
              return;
            }

            proceedOrRedirect(
              next,
              limitedUserRedirect() ?? await applyDealerIdQueryString(to, from),
            );
          },
          meta: {
            title: 'Active Selling',
            shortTitle: 'Selling',
            noAuctionsDisplayName: 'Active Selling',
            noAuctionsHeader: 'No Active Selling Auctions',
            noAuctionsMessage: 'You don\'t currently have any active selling auctions. When you do, they\'ll appear here.',
            linkText: 'Selling',
            sellerOnly: true,
            allowAuctionCommonModals: true,
          },
        },
      ],
    },
    {
      path: 'pendingproxy',
      name: 'MyAcvPendingProxy',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
      meta: {
        title: 'Pending Proxy',
        shortTitle: 'Pending Proxy',
        description: 'Auctions with a persistent proxy placed',
        noAuctionsDisplayName: 'Pending Proxy',
        noAuctionsHeader: 'No Active Pending Proxy Bids',
        noAuctionsMessage: 'You don\'t currently have any active pending proxy bids. When you do, they\'ll appear here.',
        linkText: 'Pending<span class="hidden-xs">&nbsp;Proxy</span>',
        allowAuctionCommonModals: true,
      },
    },
    {
      path: 'negotiations',
      name: 'MyAcvNegotiations',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      redirect: '/myacv/negotiations/buying',
      meta: {
        title: 'In Negotiation',
        shortTitle: 'In Negotiation',
        description: 'Auctions that have a pending offer or are in active negotiation',
        linkText: 'In Negotiation',
      },
      children: [
        {
          path: 'buying',
          name: 'MyAcvNegotiationsBuying',
          component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
          meta: {
            title: 'In Negotiation - Buying',
            shortTitle: 'Buying',
            noAuctionsDisplayName: 'Active Buying Negotiations',
            noAuctionsHeader: 'No Active Buying Negotiations ',
            noAuctionsMessage: 'You don\'t currently have any active buying offers or negotiations. When you do, they\'ll appear here.',
            linkText: 'Buying',
          },
          async beforeEnter(to, from, next) {
            proceedOrRedirect(
              next,
              limitedUserRedirect() ?? await applyDealerIdQueryString(to, from),
            );
          },
        },
        {
          path: 'selling',
          name: 'MyAcvNegotiationsSelling',
          component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
          meta: {
            title: 'In Negotiation - Selling',
            shortTitle: 'Selling',
            noAuctionsDisplayName: 'Active Selling Negotiations',
            noAuctionsHeader: 'No Active Selling Negotiations',
            noAuctionsMessage: 'You don\'t currently have any active selling offers or negotiations. When you do, they\'ll appear here.',
            linkText: 'Selling',
            sellerOnly: true,
          },
          async beforeEnter(to, from, next) {
            proceedOrRedirect(
              next,
              limitedUserRedirect() ?? await applyDealerIdQueryString(to, from),
            );
          },
        },
      ],
    },
    {
      path: 'offers',
      name: 'MyAcvMakeOffer',
      redirect: '/myacv/offers/received',
      component: () => import('@/views/MyAcv/Offers/MyAcvActionableOffers.vue'),
      async beforeEnter(to, from, next) {
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
      meta: {
        title: 'Offers',
        shortTitle: 'Offers',
        description: 'Pending Selling offers',
        noAuctionsDisplayName: 'Active Offers',
        noAuctionsHeader: 'No Active Offers',
        noAuctionsMessage: 'You don\'t currently have any active selling offers. When you do, they\'ll appear here.',
        linkText: 'Offers',
        hasExport: false,
        divider: false,
      },
      children: [
        {
          path: 'received',
          name: 'MyAcvReceivedOffers',
          component: () => import('@/views/MyAcv/Offers/ReceivedOffers.vue'),
          async beforeEnter(to, from, next) {
            const offersDashboardUiTweaks = await store.dispatch(
              'getFlag',
              store.state.featureFlags.featureFlagSlugs.oNOffersDashboardUiTweaks,
            );

            if (offersDashboardUiTweaks) {
              const userId = store.getters.currentUser?.id;
              const savedTab = userId
                ? localStorage.getItem(getOffersActiveTabKey(userId))
                : null;
              const targetTab = savedTab || getDefaultOffersTab(store.getters.currentUser);
              return next({ name: targetTab === 'selling' ? 'SellerOffers' : 'BuyerOffers' });
            }

            return next({ name: 'BuyerOffers' });
          },
          props: {
            viewType: viewTypeConstants.SELLING,
          },
          meta: {
            title: 'Offers',
            hideFromView: true,
          },
        },
        {
          path: 'received/buying',
          name: 'BuyerOffers',
          component: () => import('@/views/MyAcv/Offers/BuyerOffers.vue'),
          props: {
            viewType: viewTypeConstants.BUYING,
          },
          meta: {
            title: 'Buyer Offers',
            hideFromView: true,
          },
        },
        {
          path: 'received/selling',
          name: 'SellerOffers',
          component: () => import('@/views/MyAcv/Offers/SellerOffers.vue'),
          props: {
            viewType: viewTypeConstants.SELLING,
          },
          meta: {
            title: 'Seller Offers',
            hideFromView: true,
          },
        },
      ],
    },
    {
      path: 'bidmatrix',
      name: 'MyAcvBidMatrix',
      component: () => import('@/views/MyAcv/MyAcvBidMatrix.vue'),
      meta: {
        title: 'S.A.M. Buying Rules',
        shortTitle: 'S.A.M. Buying Rules',
        description: 'S.A.M. Buying Rules',
        linkText: 'S.A.M. Buying Rules',
        divider: true,
      },
    },
    {
      path: 'billing',
      name: 'MyAcvBilling',
      component: () => import('@/views/MyAcv/MyAcvBilling.vue'),
      redirect: '/myacv/billing/invoices',
      beforeEnter(to, from, next) {
        const redirect = limitedUserRedirect();
        if (redirect) {
          next(redirect);
        } else if (store.getters['myAcvInvoices/isInvoicesMenuEnabled']) {
          next();
        } else {
          next({ name: 'MyAcvSold' });
        }
      },
      meta: {
        title: 'Billing',
        shortTitle: 'Billing',
        description: 'Billing',
        linkText: 'Billing',
        badge: 'New',
      },
      children: [
        {
          path: 'invoices',
          name: 'MyAcvBillingInvoices',
          component: () => import('@/views/MyAcv/Invoices/MyAcvInvoices.vue'),
          redirect: '/myacv/billing/invoices/open',
          meta: {
            title: 'Invoices',
            shortTitle: 'Invoices',
            linkText: 'Invoices',
            alwaysShow: true,
          },
          children: [
            {
              path: 'open',
              name: 'MyAcvOpenInvoices',
              component: () => import('@/views/MyAcv/Invoices/OpenInvoices.vue'),
              props: {
                viewType: 'open',
              },
              meta: {
                title: 'Open Invoices',
                hideFromView: true,
              },
            },
            {
              path: 'closed',
              name: 'MyAcvClosedInvoices',
              component: () => import('@/views/MyAcv/Invoices/ClosedInvoices.vue'),
              props: {
                viewType: 'closed',
              },
              meta: {
                title: 'Closed Invoices',
                hideFromView: true,
              },
            },
          ],
        },
      ],
    },
    {
      path: 'won',
      name: 'MyAcvWonParent',
      component: {
        render: () => h(RouterView, null, {
          default: ({ Component }) => (Component ? h(KeepAlive, [h(Component)]) : null),
        }),
      },
      redirect: { name: 'MyAcvWon' },
      meta: {
        title: 'Won Auctions',
        shortTitle: 'Won',
        description: 'Auctions you\'ve previously won',
        linkText: 'Won<span class="hidden-xs">&nbsp;Auctions</span>',
        hasExport: true,
      },
      children: [
        {
          path: '',
          name: 'MyAcvWon',
          component: () => import('@/views/MyAcv/MyAcvWonAuctions.vue'),
          async beforeEnter(to, from, next) {
            proceedOrRedirect(
              next,
              limitedUserRedirect() ?? await applyDealerIdQueryString(to, from),
            );
          },
          meta: {
            title: 'Won Auctions',
            linkText: 'Won<span class="hidden-xs">&nbsp;Auctions</span>',
            hasExport: true,
            hideFromView: true,
            keepAlive: true,
          },
        },
        {
          path: 'auction/:auctionId',
          name: 'MyAcvWonAuction',
          component: () => import('@/views/MyAcv/MyAcvWonAuctionDetail.vue'),
          async beforeEnter(to, from, next) {
            const redirect = limitedUserRedirect();
            const useWonPageRework = await store.dispatch(
              'getFlag',
              store.state.featureFlags.featureFlagSlugs.useMyAcvWonPageRework,
            );
            if (redirect) {
              next(redirect);
            } else if (useWonPageRework) {
              next();
            } else {
              next({ path: '/myacv/won' });
            }
          },
          meta: {
            title: 'Won Auction Detail',
            linkText: 'Won Auctions',
            shortTitle: 'Won Auction',
            description: 'Detailed information about the won auction',
            hideFromView: true,
          },
        },
      ],
    },
    {
      path: 'sold',
      name: 'MyAcvSold',
      component: () => import('@/views/MyAcv/MyAcvAuctionList.vue'),
      async beforeEnter(to, from, next) {
        proceedOrRedirect(next, limitedUserRedirect() ?? await applyDealerIdQueryString(to, from));
      },
      meta: {
        title: 'Sold Auctions',
        shortTitle: 'Sold',
        description: 'Auctions you\'ve previously sold',
        linkText: 'Sold<span class="hidden-xs">&nbsp;Auctions</span>',
        hasExport: true,
        sellerOnly: true,
      },
    },
    {
      path: 'performance',
      name: 'MyAcvSellerPerformance',
      component: () => import('@/views/MyAcv/MyAcvSellerPerformance.vue'),
      async beforeEnter(to, from, next) {
        const redirect = await applyDealerIdQueryString(to, from);
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.myAcvOmniPerformance,
        );
        if (redirect) {
          next(redirect);
        } else if (routeEnabled) {
          next();
        } else {
          next({ name: 'MyAcvActiveBuying' });
        }
      },
      meta: {
        title: 'Performance',
        shortTitle: 'Performance',
        description: 'Performance Dashboard',
        linkText: 'Performance',
      },
    },
    {
      path: 'fee-schedule',
      name: 'MyAcvFeeSchedule',
      component: () => import('@/views/MyAcv/FeeSchedule/MyAcvFeeSchedule.vue'),
      meta: {
        title: 'Fee Schedule',
        shortTitle: 'Fee Schedule',
        description: 'Fee Schedule information',
        linkText: 'Fee Schedule',
        hideSidebar: true,
      },
      async beforeEnter(to, from, next) {
        const routeEnabled = await store.dispatch(
          'getFlag',
          store.state.featureFlags.featureFlagSlugs.enableNewFeeSchedulePage,
        );
        if (routeEnabled) {
          next();
        } else {
          next({ path: '/myacv' });
        }
      },
    },
  ],
}];

export default myAcvRoutes;
